// client/src/intermission/IntermissionOverlay.js
export class IntermissionOverlay {
  constructor(scene) {
    this.scene = scene;
    this.worldCamera = scene.worldCamera;
    this.uiCamera = scene.uiCamera;
    this.elements = [];
    this.percentTextById = new Map(); // playerId -> text object (for live updates)
    this.countdownText = null;
    this.contentContainer = null; // Main container for all overlay content
  }

  show(screen, players, itemsById, predictedChances) {
    this.destroy();

    // Add a delay to ensure UI camera dimensions are fully updated after canvas resize
    // This helps with timing issues when sidebar is toggled
    this.scene.time.delayedCall(100, () => {
      // Get fresh UI camera dimensions right before creating overlay
      const width = this.uiCamera ? this.uiCamera.width : 1440;
      const height = this.uiCamera ? this.uiCamera.height : 900;

      console.log(`🎯 IntermissionOverlay: Creating overlay with UI camera ${width}x${height}`);

      this.createOverlayContent(screen, players, itemsById, predictedChances, width, height);
    });
  }

  _applyWorldIgnore() {
    try {
      if (this.worldCamera && this.worldCamera.ignore) {
        if (this.contentContainer) {
          this.worldCamera.ignore(this.contentContainer);
          const children = (this.contentContainer.list || []);
          children.forEach(child => {
            try { this.worldCamera.ignore(child); } catch (_) {}
          });
        }
      }
    } catch (_) {}
  }

  createOverlayContent(screen, players, itemsById, predictedChances, width, height) {
    // Create background rectangle directly at UI camera center (like other UI elements)
    const margin = 45;
    const overlayW = width - margin * 10;
    const overlayH = height - margin * 2;
    const bg = this.scene.add.rectangle(width / 2, height / 2, overlayW, overlayH, 0x000000, 0.8);
    bg.setDepth(3000);
    this.elements.push(bg);

    // Ensure world camera ignores the overlay so it renders on UI camera
    if (this.worldCamera && this.worldCamera.ignore) {
      this.worldCamera.ignore(bg);
    }

    // Fade in animation (like PreBattleScene)
    bg.setAlpha(0);
    this.scene.tweens.add({
      targets: bg,
      alpha: 0.7,
      duration: 800,
      ease: 'Power2'
    });

    // Create content container for text and other elements (positioned relative to background)
    this.contentContainer = this.scene.add.container(width / 2, height / 2);
    this.contentContainer.setDepth(3001);
    this.elements.push(this.contentContainer);

    //const { width, height } = this.scene.cameras.main;
    
    // Create semi-transparent overlay to darken battlefield background
    /*this.background = this.scene.add.rectangle(width / 2, height / 2, width, height, 0xFF172A);
    this.background.setAlpha(0.7); // Semi-transparent to show battlefield through
    this.background.setDepth(3000);
    if (this.worldCamera && this.worldCamera.ignore) {
      this.worldCamera.ignore(this.background);
    } else {
      console.log('🔧 DEBUG: World camera not found');
    }
    this.elements.push(this.background);*/

    // Title (positioned relative to container center)
    const title = this.scene.add.text(0, -overlayH / 2 + 50, screen.title || 'Intermission', {
      fontFamily: 'Arial Black, Arial, sans-serif',
      fontSize: '42px',
      color: '#FFD700',
      stroke: '#000000',
      strokeThickness: 4,
      align: 'center'
    }).setOrigin(0.5).setDepth(1); // Relative depth within container
    this.contentContainer.add(title);

    if (screen.subtitle) {
      const sub = this.scene.add.text(0, -overlayH / 2 + 100, screen.subtitle, {
        fontFamily: 'Arial',
        fontSize: '28px',
        color: '#FFFFFF',
        stroke: '#000000',
        strokeThickness: 2,
        align: 'center'
      }).setOrigin(0.5).setDepth(1);
      this.contentContainer.add(sub);
    }

    const content = screen.content || { type: 'message' };
    const topY = -overlayH / 2 + 160;

    if (content.type === 'message') {
      const msg = this.scene.add.text(0, 0, content.message || '', {
        fontFamily: 'Impact, Arial Black, sans-serif',
        fontSize: '36px',
        color: '#ff66ff',
        stroke: '#000000',
        strokeThickness: 5,
        align: 'center'
      }).setOrigin(0.5).setDepth(1);
      this.contentContainer.add(msg);
      // Ensure UI-only rendering even in early-return branch
      this._applyWorldIgnore();
      return;
    }

    if (content.type === 'messageImage') {
      // Determine image display size first to vertically center message + image as a stack
      const size = Math.min(overlayW * 0.5, overlayH * 0.5);
      const messageY = Math.max(-overlayH / 2 + 100, -(size / 2) - 60);
      const imageY = Math.min(40, overlayH / 2 - (size / 2) - 40);

      const msg = this.scene.add.text(0, messageY, content.message || '', {
        fontFamily: 'Impact, Arial Black, sans-serif',
        fontSize: '36px',
        color: '#00ffff',
        stroke: '#000000',
        strokeThickness: 5,
        align: 'center'
      }).setOrigin(0.5).setDepth(1);
      this.contentContainer.add(msg);

      if (content.image) {
        // Fallback to preloaded key if texture is not loaded
        const key = (this.scene.textures && this.scene.textures.exists(content.image))
          ? content.image
          : 'preloadBackground';
        const img = this.scene.add.image(0, imageY, key);
        img.setDisplaySize(size, size);
        img.setDepth(1);
        this.contentContainer.add(img);
      }
      // Ensure UI-only rendering even in early-return branch
      this._applyWorldIgnore();
      return;
    }

    if (content.type === 'playerList' || content.type === 'playerListWithAction') {
      const alive = players.filter(p => p.isAlive);
      const rowStartY = topY;
      const rowH = 60; // Increased from 42
      const maxItems = 10; // Hard cap at 10

      // Calculate centered starting X for the player list with columnar layout
      const listWidth = 400; // Wider to accommodate columns
      const listStartX = -listWidth / 2; // Relative to container center

      // Define column positions (fixed widths for alignment) - relative to container
      const colPct = listStartX - 50; // Percentage column (playerListWithAction only)
      const colIcon = content.type === 'playerListWithAction' ? listStartX + 80 : listStartX; // Icon column
      const colName = colIcon + 45; // Name column (fixed width)
      const colHealth = colName + 180; // Health bar column
      const colInventory = colHealth + 115; // Inventory column

      alive.forEach((p, idx) => {
        const rowY = rowStartY + idx * rowH;
        if (rowY > overlayH / 2 - 100) return;

        // Column 1: Percentage (playerListWithAction only)
        if (content.type === 'playerListWithAction') {
          const pid = p.walletAddress || p.name;
          const pct = predictedChances?.get(pid) ?? 0;
          const pctText = this.scene.add.text(colPct, rowY, `${pct}%`, {
            fontFamily: 'Arial Black',
            fontSize: '40px',
            color: '#ffff00',
            stroke: '#000000',
            strokeThickness: 4
          }).setOrigin(0, 0).setDepth(1);
          this.contentContainer.add(pctText);
          this.percentTextById.set(pid, pctText);
        }

        // Column 2: Player icon
        const icon = this.scene.add.image(colIcon, rowY + 15, p.sprite.texture.key);
        icon.setDisplaySize(50, 50);
        icon.setDepth(1);
        this.contentContainer.add(icon);

        // Column 3: Player name (left-aligned within fixed column width)
        const nameText = this.scene.add.text(colName, rowY, p.name, {
          fontFamily: 'Arial Black',
          fontSize: '22px',
          color: '#ffffff',
          stroke: '#000000',
          strokeThickness: 4
        }).setDepth(1);
        this.contentContainer.add(nameText);

        // Column 4: Health bar
        const hbBg = this.scene.add.graphics().setDepth(1);
        hbBg.fillStyle(0x000000, 0.6);
        hbBg.fillRect(colHealth, rowY + 15, 100, 15);
        const hb = this.scene.add.graphics().setDepth(1);
        const hpPct = Math.max(0, Math.min(1, p.health / p.maxHealth));
        const color = hpPct < 0.33 ? 0xff3333 : hpPct < 0.66 ? 0xffff33 : 0x33ff33;
        hb.fillStyle(color, 1);
        hb.fillRect(colHealth, rowY + 15, 100 * hpPct, 15);
        this.contentContainer.add(hbBg);
        this.contentContainer.add(hb);

        // Column 5: Inventory icons
        if (content.showInventory) {
          const inv = p.inventory || [];
          const toShow = inv.slice(0, maxItems);
          toShow.forEach((entry, i) => {
            const item = itemsById?.get(entry.itemId);
            if (!item) return;
            const iv = this.scene.add.image(colInventory + i * 20, rowY + 18, item.iconKey);
            iv.setDisplaySize(48, 48);
            iv.setDepth(1);
            this.contentContainer.add(iv);
          });
        }
      });

      if (content.type === 'playerListWithAction' && content.showCountdown) {
        const cd = this.scene.add.text(0, overlayH / 2 - 50, 'Closing soon...', {
          fontFamily: 'Arial Black',
          fontSize: '24px', // Increased from 18px
          color: '#ffff00',
          stroke: '#000000',
          strokeThickness: 4
        }).setOrigin(0.5).setDepth(1);
        this.contentContainer.add(cd);
        this.countdownText = cd;
      }
    }

    // Ensure every overlay element is ignored by the world camera (render only on UI camera)
    this._applyWorldIgnore();
  }

  updatePercent(playerId, percent) {
    const txt = this.percentTextById.get(playerId);
    if (txt) txt.setText(`${percent}%`);
  }

  destroy() {
    this.percentTextById.clear();
    if (this.elements && this.elements.length) {
      this.elements.forEach(el => { if (el && el.destroy) el.destroy(); });
    }
    this.elements = [];
    this.contentContainer = null;
    this.countdownText = null;
  }

  // Method to recalculate and refresh overlay positioning
  refreshPositioning() {
    
    if (this.elements.length === 0) return; // No overlay to refresh
    
    // Get current dimensions
    let camW, camH;
    if (this.scene.game.canvas) {
      camW = this.scene.game.canvas.width;
      camH = this.scene.game.canvas.height;
    } else if (this.scene.scale) {
      camW = this.scene.scale.width;
      camH = this.scene.scale.height;
    } else {
      camW = this.scene.cameras.main.width;
      camH = this.scene.cameras.main.height;
    }
    
    const margin = 40;
    const centerX = camW / 2;
    const centerY = camH / 2;
    
    console.log(`🔄 IntermissionOverlay: Refreshing positioning to (${centerX}, ${centerY}) with dimensions ${camW}x${camH}`);
    
    // Note: For a complete refresh, you would need to recalculate all element positions
    // This is a placeholder for future enhancement if needed
  }
}
